import { useQuery } from '@tanstack/react-query';
import { useWorkspaceScope } from '@/contexts/useWorkspaceScope';
import { useAuth } from '@/lib/auth';
import { ResultsService, type WeeklyReport, type WeeklyStats } from '@/data/results.service';

/**
 * Hook to fetch weekly results
 */
export function useWeeklyResults(weeks: number = 2) {
  const { activeWorkspaceId } = useWorkspaceScope();
  const { user } = useAuth();

  return useQuery({
    queryKey: ['weekly-results', activeWorkspaceId, user?.id, weeks],
    queryFn: async (): Promise<WeeklyReport[]> => {
      if (!activeWorkspaceId) throw new Error('No workspace');
      
      return ResultsService.getWeeklyReports(activeWorkspaceId, user?.id || null, weeks);
    },
    enabled: !!activeWorkspaceId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to get current week stats
 */
export function useCurrentWeekStats() {
  const { activeWorkspaceId } = useWorkspaceScope();
  const { user } = useAuth();

  return useQuery({
    queryKey: ['current-week-stats', activeWorkspaceId, user?.id],
    queryFn: async (): Promise<WeeklyStats> => {
      if (!activeWorkspaceId) throw new Error('No workspace');
      
      const weekStart = ResultsService.getWeekStart();
      return ResultsService.calculateWeeklyStats(activeWorkspaceId, user?.id || null, weekStart);
    },
    enabled: !!activeWorkspaceId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

